import {EditableSetting, UpdatingSetting} from "../../data/models/UpdatingSetting";
import * as React from "react";
import {STATUS_TEXTS, StatusChip} from "../../components/StatusChip";
import {UpdatingConfigurationData} from "./types";
import {TableColumn, TABLES_INDEXES, TABLES_NAMES} from "../CurrentConfigurationTable/types";
import {createColumn, createColumnsForCurrentSettings} from "../CurrentConfigurationTable/utils";
import {getFormattedDate, getStatus, getUnitOfMeasure, isUndefinedOrNullValue} from "../../ReadAndWrite/utils";
import {RegisterInfo} from "../../components/RegisterInfo";


export const getStatusBadge = (updatingSetting: UpdatingSetting,
                               isValidating: boolean): React.ReactNode => {
    const status = getStatus(updatingSetting, isValidating);

    if (status === STATUS_TEXTS.ERROR) {
        return <p className={"error-message"}>{updatingSetting?.errorMessage}</p>
    }

    return <StatusChip status={getStatus(updatingSetting, isValidating)}/>
}


export const mapSettingsToTableData = (settings: EditableSetting[],
                                       updatingSettings: UpdatingSetting[],
                                       isValidating: boolean): UpdatingConfigurationData[] => {
    return settings.map((setting) => {
        const updatingValue: UpdatingSetting = getUpdatingSettingFromRegister(updatingSettings, setting.reg)
        const currentValue = !isUndefinedOrNullValue(updatingValue?.receivedValue)
            ? updatingValue?.receivedValue + getUnitOfMeasure(setting?.um)
            : !isUndefinedOrNullValue(setting?.value)
                ? setting?.value + getUnitOfMeasure(setting?.um)
                : "-"


        return {
            info: <RegisterInfo message={setting?.notes} isVisible={setting?.notes?.length > 0}/>,
            label: setting?.label ?? "-",
            name: <p className={"font-weight-bold"}>{setting?.name ?? "-"}</p>,
            default: !isUndefinedOrNullValue(setting?.default) ? setting?.default + getUnitOfMeasure(setting?.um) : "-",
            min: setting?.min ?? "-",
            max: setting?.max ?? "-",
            lastUpdate: setting?.updatedAt ? getFormattedDate({dateToFormat: setting?.updatedAt}) : "-",
            current: <p className={"font-weight-bold"}>{currentValue}</p>,
            new: !isUndefinedOrNullValue(setting?.newValue) ? setting?.newValue + getUnitOfMeasure(setting?.um) : "-",
            status: getStatusBadge(updatingValue, isValidating),
        }
    });
}

export const createColumnsForUpdatingTable = (getTableHeadersName: (name: TABLES_NAMES) => string): TableColumn[] => {
    return [
        ...createColumnsForCurrentSettings(getTableHeadersName),
        createColumn(getTableHeadersName(TABLES_NAMES.NEW), TABLES_INDEXES.NEW, TABLES_INDEXES.NEW, 50),
        createColumn(getTableHeadersName(TABLES_NAMES.STATUS), TABLES_INDEXES.STATUS, TABLES_INDEXES.STATUS, 50),
    ];
}

export const getUpdatingSettingFromRegister = (updatingInfo: UpdatingSetting[], register: number): UpdatingSetting => {
    return updatingInfo?.find((setting) => setting?.reg === register);
}

export const containsUpdatingSettings = (editableSettings: EditableSetting[],
                                         updatingSettings: UpdatingSetting[],
                                         isValidating: boolean): boolean => {
    return editableSettings.some((setting) => {
            const updatingSetting = getUpdatingSettingFromRegister(updatingSettings, setting.reg);
            return getStatus(updatingSetting, isValidating) !== STATUS_TEXTS.NONE;
        }
    )
}

export const containsAllDoneSettings = (editableSettings: EditableSetting[], updatingSettings: UpdatingSetting[],
                                        isValidating: boolean,
                                        name: string): boolean => {
    if (containsAllNonStatusSettings(editableSettings, updatingSettings, isValidating)) {
        return false
    }

    return editableSettings.every((setting) => {
            const updatingSetting = getUpdatingSettingFromRegister(updatingSettings, setting.reg);
            return getStatus(updatingSetting, isValidating) === STATUS_TEXTS.COMPLETED
                || getStatus(updatingSetting, isValidating) === STATUS_TEXTS.NONE;
        }
    )
}

export const containsAllNonStatusSettings = (editableSettings: EditableSetting[],
                                             updatingSettings: UpdatingSetting[],
                                             isValidating: boolean): boolean => {
    return editableSettings.every((setting) => {
            const updatingSetting = getUpdatingSettingFromRegister(updatingSettings, setting.reg);
            return getStatus(updatingSetting, isValidating) === STATUS_TEXTS.NONE;
        }
    )
}